import React, { useState, useEffect } from 'react';
import { Upload, X, Image as ImageIcon, Building2, Truck, Bus, Car, Briefcase, Mail, Phone, FileCheck, Trash2, Loader2, Sliders } from 'lucide-react';
import { apiClient, FleetSubtype, ConsentMethod } from '../services/api';
import { toast } from 'react-hot-toast';
import ScoringEditor from './ScoringEditor';

interface OrganisationBrandingProps {
    user: any;
}

type OrganisationType = 'fleet' | 'recruitment' | 'compliance';

interface Organisation {
    id: string;
    name: string;
    type: OrganisationType;
    subtype: FleetSubtype;
    contactEmail: string;
    contactPhone?: string;
    logoUrl?: string;
    primaryColor: string;
    secondaryColor: string;
    consentMethod: ConsentMethod;
}

const ORG_TYPE_LABELS: Record<OrganisationType, string> = {
    fleet: 'Fleet Operator',
    recruitment: 'Recruitment Agency',
    compliance: 'Compliance Service',
};

const SUBTYPE_CONFIG: Record<FleetSubtype, { label: string; icon: React.ElementType; description: string }> = {
    bus: { label: 'Bus Operator', icon: Bus, description: 'PCV bus services with Category D requirements' },
    coach: { label: 'Coach Operator', icon: Bus, description: 'PCV coach services with Category D requirements' },
    haulage: { label: 'Haulage / HGV', icon: Truck, description: 'HGV/LGV operations with Category C requirements' },
    car_fleet: { label: 'Car Fleet', icon: Car, description: 'Company car fleet with standard Category B' },
    general: { label: 'General', icon: Briefcase, description: 'Universal compliance checks' },
};

const CARD = 'bg-white rounded-xl border border-gray-200/80 shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)]';

const OrganisationBranding: React.FC<OrganisationBrandingProps> = ({ user }) => {
    const [organisation, setOrganisation] = useState<Organisation | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [selectedConsentMethod, setSelectedConsentMethod] = useState<ConsentMethod>('email');
    const [isSavingConsentMethod, setIsSavingConsentMethod] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [checkSettings, setCheckSettings] = useState<{ defaultIncludeCPC: boolean; defaultIncludeTacho: boolean }>({ defaultIncludeCPC: false, defaultIncludeTacho: false });
    const [savedCheckSettings, setSavedCheckSettings] = useState<{ defaultIncludeCPC: boolean; defaultIncludeTacho: boolean }>({ defaultIncludeCPC: false, defaultIncludeTacho: false });
    const [isLoadingCheckSettings, setIsLoadingCheckSettings] = useState(false);
    const [isSavingCheckSettings, setIsSavingCheckSettings] = useState(false);

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

    useEffect(() => {
        loadOrganisation();
        if (user?.organisation?.automatedChecks) {
            loadCheckSettings();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadOrganisation = async () => {
        try {
            setIsLoading(true);
            const data = await apiClient.getOrganisation();
            setOrganisation(data);
            setSelectedConsentMethod(data.consentMethod || 'email');
        } catch (error) {
            toast.error('Failed to load organisation details');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveConsentMethod = async () => {
        try {
            setIsSavingConsentMethod(true);
            await apiClient.updateOrganisation({ consentMethod: selectedConsentMethod });
            setOrganisation(prev => prev ? { ...prev, consentMethod: selectedConsentMethod } : null);
            toast.success('Consent method updated successfully');
        } catch (error: any) {
            toast.error(error.message || 'Failed to update consent method');
        } finally {
            setIsSavingConsentMethod(false);
        }
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
        if (!validTypes.includes(file.type)) { toast.error('Please select a PNG, JPG, JPEG, or SVG file'); return; }
        if (file.size > 2 * 1024 * 1024) { toast.error('File size must be less than 2MB'); return; }
        setSelectedFile(file);
        const reader = new FileReader();
        reader.onloadend = () => setPreviewUrl(reader.result as string);
        reader.readAsDataURL(file);
    };

    const handleUpload = async () => {
        if (!selectedFile) return;
        try {
            setIsUploading(true);
            const formData = new FormData();
            formData.append('logo', selectedFile);
            await apiClient.uploadOrganisationLogo(formData);
            toast.success('Logo uploaded successfully');
            await loadOrganisation();
            setSelectedFile(null);
            setPreviewUrl(null);
        } catch (error: any) {
            toast.error(error.message || 'Failed to upload logo');
        } finally {
            setIsUploading(false);
        }
    };

    const handleDelete = async () => {
        try {
            setIsLoading(true);
            await apiClient.deleteOrganisationLogo();
            toast.success('Logo deleted successfully');
            setOrganisation(prev => prev ? { ...prev, logoUrl: undefined } : null);
            setShowDeleteModal(false);
        } catch (error: any) {
            toast.error(error.message || 'Failed to delete logo');
        } finally {
            setIsLoading(false);
        }
    };

    const cancelSelection = () => { setSelectedFile(null); setPreviewUrl(null); };

    const loadCheckSettings = async () => {
        try {
            setIsLoadingCheckSettings(true);
            const data = await apiClient.getAutomationSettings();
            const loaded = {
                defaultIncludeCPC: data.defaultIncludeCPC ?? false,
                defaultIncludeTacho: data.defaultIncludeTacho ?? false,
            };
            setCheckSettings(loaded);
            setSavedCheckSettings(loaded);
        } catch (error) {
            toast.error('Failed to load check configuration');
        } finally {
            setIsLoadingCheckSettings(false);
        }
    };

    const handleSaveCheckSettings = async () => {
        try {
            setIsSavingCheckSettings(true);
            await apiClient.updateAutomationSettings(checkSettings);
            setSavedCheckSettings(checkSettings);
            toast.success('Check configuration saved');
        } catch (error: any) {
            toast.error(error.message || 'Failed to save check configuration');
        } finally {
            setIsSavingCheckSettings(false);
        }
    };

    const subtypeConfig = organisation?.subtype ? SUBTYPE_CONFIG[organisation.subtype] : SUBTYPE_CONFIG.general;
    const SubtypeIcon = subtypeConfig.icon;

    if (isLoading && !organisation) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin h-8 w-8 border-4 border-t-transparent rounded-full"
                     style={{ borderColor: '#69BE28 transparent transparent transparent' }}></div>
            </div>
        );
    }

    return (
        <>
        <div className="space-y-6">

            {/* Organisation Details */}
            <div className={`${CARD} p-6`}>
                <div className="flex items-center gap-3 mb-5">
                    <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(105,190,40,0.12)' }}>
                        <Building2 className="w-5 h-5" style={{ color: '#69BE28' }} />
                    </div>
                    <h2 className="text-lg font-medium text-gray-900">Organisation Details</h2>
                </div>

                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                    <div>
                        <p className="text-sm text-gray-500 mb-0.5">Organisation Name</p>
                        <p className="text-sm font-medium text-gray-900">{organisation?.name || '-'}</p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500 mb-0.5">Account Type</p>
                        <p className="text-sm font-medium text-gray-900">
                            {organisation?.type ? ORG_TYPE_LABELS[organisation.type] : '-'}
                        </p>
                    </div>
                    {organisation?.type === 'fleet' && (
                        <div>
                            <p className="text-sm text-gray-500 mb-0.5">Industry Type</p>
                            <div className="flex items-center gap-1.5">
                                <SubtypeIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                <p className="text-sm font-medium text-gray-900">{subtypeConfig.label}</p>
                            </div>
                            <p className="text-xs text-gray-400 mt-0.5">{subtypeConfig.description}</p>
                        </div>
                    )}
                    <div>
                        <p className="text-sm text-gray-500 mb-0.5">Contact Email</p>
                        <div className="flex items-center gap-1.5">
                            <Mail className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                            <p className="text-sm font-medium text-gray-900">{organisation?.contactEmail || '-'}</p>
                        </div>
                    </div>
                    {organisation?.contactPhone && (
                        <div>
                            <p className="text-sm text-gray-500 mb-0.5">Contact Phone</p>
                            <div className="flex items-center gap-1.5">
                                <Phone className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                <p className="text-sm font-medium text-gray-900">{organisation.contactPhone}</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Check Configuration */}
            <div className={`${CARD} p-6`}>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(105,190,40,0.12)' }}>
                            <Sliders className="w-5 h-5" style={{ color: '#69BE28' }} />
                        </div>
                        <h2 className="text-lg font-medium text-gray-900">Check Configuration</h2>
                    </div>
                    <p className="text-xs text-gray-500 mb-5">
                        Select which additional checks are included by default for this organisation across all check types.
                    </p>

                    {isLoadingCheckSettings ? (
                        <div className="flex items-center gap-2 text-sm text-gray-500 mb-5">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Loading...
                        </div>
                    ) : (
                        <div className="space-y-3 mb-5">
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={checkSettings.defaultIncludeCPC}
                                    onChange={(e) => setCheckSettings({ ...checkSettings, defaultIncludeCPC: e.target.checked })}
                                    className="w-4 h-4 rounded"
                                    style={{ accentColor: '#69BE28' }}
                                />
                                <div>
                                    <p className="text-sm font-medium text-gray-900">CPC checks</p>
                                    <p className="text-xs text-gray-500">Include Certificate of Professional Competence data in licence checks</p>
                                </div>
                            </label>
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={checkSettings.defaultIncludeTacho}
                                    onChange={(e) => setCheckSettings({ ...checkSettings, defaultIncludeTacho: e.target.checked })}
                                    className="w-4 h-4 rounded"
                                    style={{ accentColor: '#69BE28' }}
                                />
                                <div>
                                    <p className="text-sm font-medium text-gray-900">Tachograph card checks</p>
                                    <p className="text-xs text-gray-500">Include tachograph card data in licence checks</p>
                                </div>
                            </label>
                        </div>
                    )}

                    <button
                        onClick={handleSaveCheckSettings}
                        disabled={isSavingCheckSettings || isLoadingCheckSettings || (checkSettings.defaultIncludeCPC === savedCheckSettings.defaultIncludeCPC && checkSettings.defaultIncludeTacho === savedCheckSettings.defaultIncludeTacho)}
                        className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 inline-flex items-center"
                        style={{ backgroundColor: '#69BE28' }}
                    >
                        {isSavingCheckSettings
                            ? <><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>Saving...</>
                            : 'Save'}
                    </button>
                </div>

            {/* Risk Scoring (fleet only) */}
            {organisation?.type === 'fleet' && (
                <ScoringEditor user={user} />
            )}

            {/* Consent Method */}
            <div className={`${CARD} p-6`}>
                <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(105,190,40,0.12)' }}>
                        <FileCheck className="w-5 h-5" style={{ color: '#69BE28' }} />
                    </div>
                    <h2 className="text-lg font-medium text-gray-900">Consent Method</h2>
                </div>
                <p className="text-xs text-gray-500 mb-5">
                    Controls how driver consent is collected and verified for your organisation.
                </p>

                <div className="space-y-2 mb-5">
                    {[
                        {
                            value: 'email' as ConsentMethod,
                            title: 'Email (Default)',
                            description: 'Send a consent request link to each driver by email. Driver signs digitally via the consent form page.',
                        },
                        {
                            value: 'offline' as ConsentMethod,
                            title: 'Offline (Physical D906 Form)',
                            description: 'Drivers sign a physical D906 form. Reference number and date are recorded manually per driver.',
                        },
                        {
                            value: 'external' as ConsentMethod,
                            title: 'External (Managed via CRM / API)',
                            description: 'Consent is managed in your own system. A reference number and date must be provided when creating each driver via the API.',
                        },
                    ].map(({ value, title, description }) => (
                        <label
                            key={value}
                            className={`flex items-start gap-3 p-3.5 rounded-lg border cursor-pointer transition-colors ${
                                selectedConsentMethod === value
                                    ? 'border-green-400 bg-green-50'
                                    : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                            <input
                                type="radio"
                                name="consentMethod"
                                value={value}
                                checked={selectedConsentMethod === value}
                                onChange={() => setSelectedConsentMethod(value)}
                                className="mt-0.5"
                            />
                            <div>
                                <p className="text-sm font-medium text-gray-900">{title}</p>
                                <p className="text-xs text-gray-500">{description}</p>
                            </div>
                        </label>
                    ))}
                </div>

                {selectedConsentMethod === 'external' && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5">
                        <p className="text-xs text-amber-800">
                            <strong>Important:</strong> By selecting this option you confirm that your organisation has a valid and documented process for obtaining and storing driver consent before any licence checks are performed.
                        </p>
                    </div>
                )}

                <button
                    onClick={handleSaveConsentMethod}
                    disabled={isSavingConsentMethod || selectedConsentMethod === organisation?.consentMethod}
                    className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 inline-flex items-center"
                    style={{ backgroundColor: '#69BE28' }}
                >
                    {isSavingConsentMethod
                        ? <><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>Saving...</>
                        : 'Save'}
                </button>
            </div>

            {/* Branding */}
            <div className={`${CARD} p-6`}>
                <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(105,190,40,0.12)' }}>
                        <ImageIcon className="w-5 h-5" style={{ color: '#69BE28' }} />
                    </div>
                    <h2 className="text-lg font-medium text-gray-900">Logo &amp; Branding</h2>
                </div>
                <p className="text-xs text-gray-500 mb-5">
                    Your logo appears in the email signature sent to drivers. PNG, JPG or SVG · Max 2MB · Transparent background recommended.
                </p>

                {organisation?.logoUrl ? (
                    <div>
                        <div className="w-48 h-28 border border-gray-200 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center mb-4">
                            <img
                                src={`${API_URL}/${organisation.logoUrl}`}
                                alt="Organisation logo"
                                className="max-w-full max-h-full object-contain p-3"
                            />
                        </div>
                        <button
                            onClick={() => setShowDeleteModal(true)}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                        >
                            <Trash2 className="w-4 h-4 mr-1.5" />
                            Remove Logo
                        </button>
                    </div>
                ) : (
                    <div>
                        {!selectedFile && (
                            <div className="flex items-center justify-center py-8 mb-4 border border-dashed border-gray-200 rounded-lg bg-gray-50">
                                <div className="text-center">
                                    <ImageIcon className="mx-auto h-8 w-8 text-gray-300 mb-2" />
                                    <p className="text-sm text-gray-500">No logo uploaded yet</p>
                                </div>
                            </div>
                        )}

                        {!selectedFile ? (
                            <div>
                                <label
                                    htmlFor="logo-upload"
                                    className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                                >
                                    <Upload className="w-4 h-4 mr-2" />
                                    Choose File
                                </label>
                                <input
                                    id="logo-upload"
                                    type="file"
                                    accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                                    onChange={handleFileSelect}
                                    className="hidden"
                                />
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div>
                                    <p className="text-xs font-medium text-gray-700 mb-2">Preview</p>
                                    <div className="w-40 h-24 border border-dashed border-gray-200 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center">
                                        {previewUrl && (
                                            <img src={previewUrl} alt="Preview" className="max-w-full max-h-full object-contain" />
                                        )}
                                    </div>
                                </div>
                                <p className="text-xs text-gray-500">
                                    {selectedFile.name} &middot; {(selectedFile.size / 1024).toFixed(0)} KB
                                </p>
                                <div className="flex gap-3">
                                    <button
                                        onClick={handleUpload}
                                        disabled={isUploading}
                                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 hover:opacity-90 transition-opacity"
                                        style={{ backgroundColor: '#69BE28' }}
                                    >
                                        {isUploading
                                            ? <><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>Uploading...</>
                                            : <><Upload className="w-4 h-4 mr-2" />Upload Logo</>}
                                    </button>
                                    <button
                                        onClick={cancelSelection}
                                        disabled={isUploading}
                                        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

        </div>

        {/* Remove Logo Modal */}
        {showDeleteModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
                    <div className="flex items-center justify-between p-6 border-b border-gray-200">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-red-50 rounded-lg">
                                <Trash2 className="w-5 h-5 text-red-600" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">Remove Logo?</h3>
                                <p className="text-sm text-gray-500">This action cannot be undone</p>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowDeleteModal(false)}
                            disabled={isLoading}
                            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    <div className="p-6">
                        <p className="text-sm text-gray-700 mb-4">
                            Your organisation logo will be permanently removed. This affects how your emails appear to drivers.
                        </p>
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                            <ul className="text-sm text-red-800 space-y-1 list-disc list-inside">
                                <li>The logo will be removed from all future driver consent emails</li>
                                <li>Emails will fall back to the default IntelliDrive branding</li>
                                <li>You can upload a new logo at any time</li>
                            </ul>
                        </div>
                    </div>
                    <div className="flex justify-end gap-3 px-6 pb-6">
                        <button
                            onClick={() => setShowDeleteModal(false)}
                            disabled={isLoading}
                            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                            Go Back
                        </button>
                        <button
                            onClick={handleDelete}
                            disabled={isLoading}
                            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center"
                        >
                            {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            {isLoading ? 'Removing...' : 'Remove Logo'}
                        </button>
                    </div>
                </div>
            </div>
        )}
        </>
    );
};

export default OrganisationBranding;
